import { AnchorButton, Classes, Intent, Label } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import classNames from 'classnames';
import * as React from 'react';
import { Tooltip } from 'vet-bones/components';
import { escapeStringForRegExp } from 'vet-bones/utils';

import { EditableLabel } from 'src/components/EditableLabel';
import { TEST_IDS } from 'src/constants/testIds';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { hasIri } from 'src/containers/ProjectDiagram/utils/hasIri';
import { projectSelector } from 'src/selectors/project';
import * as copy from 'src/templates/copy';
import { getProjectTargetModel } from 'src/utils/getProjectTargetModel';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import { validateIri } from 'src/utils/validateIri';

type ProjectDiagramEditableIriProps = {
  errorIntent?: Intent;
  header: string;
  iri: string;
  isHighlighted?: boolean;
  label: string;
  onSetIri: (iri: string, invalid: string) => void;
  onSetLabel: (label: string, invalid: string) => void;
  placeholder?: string;
  rightEl?: React.ReactNode;
  setValueOnChange?: boolean;
  testId?: string;
};

const MODEL_NAMESPACE_PLACEHOLDER =
  copy.components.projectDiagram.editableIri.modelIriNamespace;
const MODEL_NAMESPACE_PLACEHOLDER_MATCHER = new RegExp(
  `^${escapeStringForRegExp(MODEL_NAMESPACE_PLACEHOLDER)}`
);

export const ProjectDiagramEditableIri: React.VFC<
  ProjectDiagramEditableIriProps
> = ({
  errorIntent = Intent.DANGER,
  header,
  iri,
  isHighlighted,
  label,
  onSetIri,
  onSetLabel,
  placeholder,
  rightEl,
  setValueOnChange,
  testId,
}) => {
  const { projectId } = useRouterParams();
  const project = useReduxSelector(projectSelector(projectId));
  const diagram = useDiagram();
  const [isLabelHovered, setIsLabelHovered] = React.useState(false);
  const [showIri, setShowIri] = React.useState(false);

  const modelNamespace = project
    ? getProjectTargetModel(project).namespace
    : '';
  const collapsedIri = iri.replace(
    new RegExp(`^${escapeStringForRegExp(modelNamespace)}`),
    MODEL_NAMESPACE_PLACEHOLDER
  );

  const validateLabel = (label: string) => {
    if (!label) {
      return copy.components.projectDiagram.editableIri.validationLabelRequired;
    }

    return '';
  };

  const validateDiagramIri = (newIri: string) => {
    if (!diagram) {
      return '';
    }

    const expandedIri = newIri.replace(
      MODEL_NAMESPACE_PLACEHOLDER_MATCHER,
      modelNamespace
    );

    if (!setValueOnChange && expandedIri === iri) {
      return '';
    }

    const validationError = validateIri(expandedIri);
    if (validationError) {
      return validationError;
    }

    if (hasIri(expandedIri, diagram.model)) {
      return copy.components.projectDiagram.editableIri.validationIriUnique;
    }

    return '';
  };

  return (
    <div className="sd-project-diagram-editable-iri" data-testid={testId}>
      <Label>{header}</Label>
      <div
        className={classNames(
          'sd-project-diagram-editable-iri__label-container',
          {
            'sd-project-diagram-editable-iri__label-container--highlighted':
              isHighlighted,
          }
        )}
      >
        <div
          className={classNames(
            'sd-project-diagram-editable-iri__label',
            'sd-draft-highlight-candidate',
            Classes.CALLOUT,
            { 'sd-draft-highlight-candidate--highlighted': isHighlighted }
          )}
          onMouseEnter={() => setIsLabelHovered(true)}
          onMouseLeave={() => setIsLabelHovered(false)}
        >
          <EditableLabel
            errorIntent={errorIntent}
            onSetValue={(newLabel) =>
              onSetLabel(newLabel, validateLabel(newLabel))
            }
            onValidate={validateLabel}
            placeholder={placeholder}
            setValueOnChange={setValueOnChange}
            value={label}
          />
          {isLabelHovered || showIri ? (
            <Tooltip
              content={
                showIri
                  ? copy.components.projectDiagram.editableIri.hideIri
                  : copy.components.projectDiagram.editableIri.showIri
              }
            >
              <AnchorButton
                active={showIri}
                data-testid={
                  TEST_IDS.projectDiagram.entityDrawer.editableIriButton
                }
                icon={IconNames.CODE}
                minimal
                onClick={() => setShowIri(!showIri)}
              />
            </Tooltip>
          ) : null}
        </div>
        {rightEl}
      </div>

      {showIri ? (
        <div
          className={classNames(
            'sd-project-diagram-editable-iri__iri',
            Classes.TEXT_SMALL
          )}
        >
          <EditableLabel
            errorIntent={errorIntent}
            monospaceText
            onSetValue={(newIri) =>
              onSetIri(
                newIri.replace(
                  MODEL_NAMESPACE_PLACEHOLDER_MATCHER,
                  modelNamespace
                ),
                validateDiagramIri(newIri)
              )
            }
            onValidate={validateDiagramIri}
            placeholder={
              copy.components.projectDiagram.editableIri.modelIriPlaceholder
            }
            setValueOnChange={setValueOnChange}
            value={collapsedIri}
          />
        </div>
      ) : null}
    </div>
  );
};
